/**
 * StartFreshConfirmDialog - confirms starting a fresh session
 *
 * Personal mode: Warns that current session will be deleted
 * Pro mode: Warns that a new session will start, but old session is saved
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { useHasProAccess } from '../../../core/licensing';
import { AlertTriangle, FolderPlus } from 'lucide-react';

interface StartFreshConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function StartFreshConfirmDialog({
  open,
  onConfirm,
  onCancel,
}: StartFreshConfirmDialogProps) {
  const isProMode = useHasProAccess();

  return (
    <AlertDialog open={open} onOpenChange={(isOpen: boolean) => !isOpen && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            {isProMode ? (
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <FolderPlus className="h-5 w-5" />
              </div>
            ) : (
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 text-warning">
                <AlertTriangle className="h-5 w-5" />
              </div>
            )}
            <AlertDialogTitle>
              {isProMode ? 'Start a new session?' : 'Start fresh?'}
            </AlertDialogTitle>
          </div>
        </AlertDialogHeader>

        <AlertDialogDescription asChild>
          <div className="space-y-3">
            {isProMode ? (
              <>
                <p>
                  This will close all open tabs and disconnect from all servers.
                </p>
                <p className="text-muted-foreground">
                  Your current session will be saved and available in the
                  Sessions list.
                </p>
              </>
            ) : (
              <>
                <p>
                  This will <strong>permanently delete</strong> your current
                  session, close all tabs, and disconnect from all servers.
                </p>
                <p className="text-warning text-sm">
                  This action cannot be undone.
                </p>
              </>
            )}
          </div>
        </AlertDialogDescription>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid="confirm-start-fresh-button"
            className={
              isProMode
                ? ''
                : 'bg-amber-600 hover:bg-amber-700 text-white'
            }
          >
            {isProMode ? 'Start New Session' : 'Delete & Start Fresh'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
